// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect } from 'react';
import {
  Header,
  Form,
  Message,
  TextArea,
  Label,
  Button,
  Icon,
} from 'semantic-ui-react';
import { brokerageApi, EmailTemplates } from '../api/brokerageApi';

interface EmailTemplateSectionProps {
  advisorInvitationTemplate: string;
  clientInvitationTemplate: string;
  advisorInvitationSubject: string;
  clientInvitationSubject: string;
  onChange: (field: string, value: unknown) => void;
}

function EmailTemplateSection({
  advisorInvitationTemplate = '',
  clientInvitationTemplate = '',
  advisorInvitationSubject = '',
  clientInvitationSubject = '',
  onChange,
}: Readonly<EmailTemplateSectionProps>) {
  const [defaultTemplates, setDefaultTemplates] = useState<EmailTemplates | null>(null);
  const [showDefaultAdvisorTemplate, setShowDefaultAdvisorTemplate] = useState(false);
  const [showDefaultClientTemplate, setShowDefaultClientTemplate] = useState(false);

  useEffect(() => {
    const fetchDefaultTemplates = async () => {
      try {
        const templates = await brokerageApi.getDefaultEmailTemplates();
        setDefaultTemplates(templates);
      } catch (error) {
        console.error('Failed to fetch default email templates:', error);
      }
    };
    fetchDefaultTemplates();
  }, []);

  return (
    <>
      <Header as='h3' dividing style={{ marginTop: '30px' }}>
        Custom Email Templates & Subjects
        <Header.Subheader>Override default email templates with custom content</Header.Subheader>
      </Header>

      <Form.Field>
        <label htmlFor="email-advisor-subject">Advisor Invitation Email Subject</label>
        <input
          id="email-advisor-subject"
          type='text'
          name='advisorInvitationSubject'
          value={advisorInvitationSubject}
          onChange={(e) => onChange('advisorInvitationSubject', e.target.value)}
          placeholder={
            defaultTemplates?.advisorInvitationSubject || "You're invited to join {{brokerageName}}"
          }
        />
        <Label pointing>
          Custom email subject. Use <code>{'{{brokerageName}}'}</code> as placeholder.
          {defaultTemplates?.advisorInvitationSubject && (
            <span>
              {' '}
              Default: <strong>{defaultTemplates.advisorInvitationSubject}</strong>
            </span>
          )}
        </Label>
      </Form.Field>

      <Form.Field>
        <label htmlFor="email-advisor-template">
          Advisor Invitation Email Template (HTML)
          <Button
            size='mini'
            floated='right'
            type='button'
            onClick={() => setShowDefaultAdvisorTemplate(!showDefaultAdvisorTemplate)}
          >
            <Icon name={showDefaultAdvisorTemplate ? 'eye slash' : 'eye'} />
            {showDefaultAdvisorTemplate ? 'Hide' : 'View'} Default Template
          </Button>
        </label>
        <TextArea
          id="email-advisor-template"
          name='advisorInvitationTemplate'
          value={advisorInvitationTemplate}
          onChange={(e) => onChange('advisorInvitationTemplate', e.target.value)}
          placeholder='Leave empty to use default template'
          rows={8}
          style={{ fontFamily: 'monospace', fontSize: '12px' }}
        />
        <Label pointing>
          Custom HTML template for advisor invitation emails. Use Thymeleaf syntax for variables:{' '}
          <code>[[${'{advisorFirstName}'}]]</code>,{' '}
          <code>[[${'{invitationLink}'}]]</code>,{' '}
          <code>[[${'{branding.colors.primary}'}]]</code>
        </Label>
      </Form.Field>

      {showDefaultAdvisorTemplate && defaultTemplates?.advisorInvitationTemplate && (
        <Message info>
          <Message.Header>Default Advisor Invitation Template</Message.Header>
          <pre
            style={{
              background: '#f8f8f8',
              padding: '10px',
              borderRadius: '4px',
              overflow: 'auto',
              fontSize: '12px',
              maxHeight: '400px'
            }}
          >
            {defaultTemplates.advisorInvitationTemplate}
          </pre>
        </Message>
      )}

      <Form.Field>
        <label htmlFor="email-client-subject">Client Invitation Email Subject</label>
        <input
          id="email-client-subject"
          type='text'
          name='clientInvitationSubject'
          value={clientInvitationSubject}
          onChange={(e) => onChange('clientInvitationSubject', e.target.value)}
          placeholder={
            defaultTemplates?.clientInvitationSubject || 'Your access code for {{brokerageName}}'
          }
        />
        <Label pointing>
          Custom email subject. Use <code>{'{{brokerageName}}'}</code> as placeholder.
          {defaultTemplates?.clientInvitationSubject && (
            <span>
              {' '}
              Default: <strong>{defaultTemplates.clientInvitationSubject}</strong>
            </span>
          )}
        </Label>
      </Form.Field>

      <Form.Field>
        <label htmlFor="email-client-template">
          Client Invitation Email Template (HTML)
          <Button
            size='mini'
            floated='right'
            type='button'
            onClick={() => setShowDefaultClientTemplate(!showDefaultClientTemplate)}
          >
            <Icon name={showDefaultClientTemplate ? 'eye slash' : 'eye'} />
            {showDefaultClientTemplate ? 'Hide' : 'View'} Default Template
          </Button>
        </label>
        <TextArea
          id="email-client-template"
          name='clientInvitationTemplate'
          value={clientInvitationTemplate}
          onChange={(e) => onChange('clientInvitationTemplate', e.target.value)}
          placeholder='Leave empty to use default template'
          rows={8}
          style={{ fontFamily: 'monospace', fontSize: '12px' }}
        />
        <Label pointing>
          Custom HTML template for client invitation emails. Available variables:{' '}
          <code>[[${'{clientFirstName}'}]]</code>,{' '}
          <code>[[${'{otpCode}'}]]</code>,{' '}
          <code>[[${'{branding.email.headerBackgroundColor}'}]]</code>
        </Label>
      </Form.Field>

      {showDefaultClientTemplate && defaultTemplates?.clientInvitationTemplate && (
        <Message info>
          <Message.Header>Default Client Invitation Template</Message.Header>
          <pre
            style={{
              background: '#f8f8f8',
              padding: '10px',
              borderRadius: '4px',
              overflow: 'auto',
              fontSize: '12px',
              maxHeight: '400px'
            }}
          >
            {defaultTemplates.clientInvitationTemplate}
          </pre>
        </Message>
      )}
    </>
  );
}

export default EmailTemplateSection;
